import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateBox, CreateBoxVertexData } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";
import "@babylonjs/core/Meshes/thinInstanceMesh";

/**
 * Small procedural-geometry toolkit. Every part gets a per-vertex colour so whole layers can be
 * merged into one mesh with one vertex-colour material (few draw calls). All geometry is original
 * and generated from numbers; no external assets.
 */
export interface Xyz {
  x: number;
  y: number;
  z: number;
}

export interface PartOptions {
  pos?: Partial<Xyz>;
  rot?: Partial<Xyz>;
  parent?: TransformNode | null;
  flat?: boolean;
}

export function hexToColor3(hex: string): Color3 {
  return Color3.FromHexString(hex);
}

/** Give every vertex of a mesh the same colour (RGBA). */
export function colorize(mesh: Mesh, hex: string): Mesh {
  const c = hexToColor3(hex);
  const count = mesh.getTotalVertices();
  const colors = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    colors[i * 4] = c.r;
    colors[i * 4 + 1] = c.g;
    colors[i * 4 + 2] = c.b;
    colors[i * 4 + 3] = 1;
  }
  mesh.setVerticesData(VertexBuffer.ColorKind, colors, false, 4);
  return mesh;
}

export function applyPart(mesh: Mesh, hex: string, opts: PartOptions = {}): Mesh {
  if (opts.flat) mesh.convertToFlatShadedMesh();
  if (!mesh.isVerticesDataPresent(VertexBuffer.UVKind)) {
    mesh.setVerticesData(VertexBuffer.UVKind, new Float32Array(mesh.getTotalVertices() * 2), false, 2);
  }
  colorize(mesh, hex);
  const p = opts.pos ?? {};
  mesh.position.set(p.x ?? 0, p.y ?? 0, p.z ?? 0);
  const r = opts.rot ?? {};
  mesh.rotation.set(r.x ?? 0, r.y ?? 0, r.z ?? 0);
  if (opts.parent) mesh.parent = opts.parent;
  mesh.isPickable = false;
  return mesh;
}

export function box(scene: Scene, name: string, w: number, h: number, d: number, hex: string, opts: PartOptions = {}): Mesh {
  return applyPart(CreateBox(name, { width: w, height: h, depth: d }, scene), hex, opts);
}

/** Box whose bottom sits at y (y refers to the base, convenient for walls and posts). */
export function boxOnGround(scene: Scene, name: string, w: number, h: number, d: number, hex: string, x: number, yBase: number, z: number, opts: PartOptions = {}): Mesh {
  return box(scene, name, w, h, d, hex, { ...opts, pos: { x, y: yBase + h / 2, z } });
}

export interface CylinderSpec {
  height: number;
  diameter?: number;
  diameterTop?: number;
  diameterBottom?: number;
  tessellation?: number;
}

export function cylinder(scene: Scene, name: string, spec: CylinderSpec, hex: string, opts: PartOptions = {}): Mesh {
  const mesh = CreateCylinder(
    name,
    {
      height: spec.height,
      diameter: spec.diameter ?? 1,
      diameterTop: spec.diameterTop,
      diameterBottom: spec.diameterBottom,
      tessellation: spec.tessellation ?? 12,
    },
    scene,
  );
  return applyPart(mesh, hex, opts);
}

export function sphere(scene: Scene, name: string, diameter: number, segments: number, hex: string, opts: PartOptions = {}): Mesh {
  return applyPart(CreateSphere(name, { diameter, segments }, scene), hex, opts);
}

export function torus(scene: Scene, name: string, diameter: number, thickness: number, tessellation: number, hex: string, opts: PartOptions = {}): Mesh {
  return applyPart(CreateTorus(name, { diameter, thickness, tessellation }, scene), hex, opts);
}

export function plane(scene: Scene, name: string, w: number, h: number, hex: string, opts: PartOptions = {}): Mesh {
  return applyPart(CreatePlane(name, { width: w, height: h }, scene), hex, opts);
}

export interface PolyOptions extends PartOptions {
  /** Explicit outward direction for single faces (otherwise the centroid of all vertices is used). */
  outward?: Xyz;
}

/**
 * Flat-shaded mesh from convex polygon faces. Faces are re-wound so that every face points away
 * from the solid centroid (or along `outward`), using Babylon's front-face convention
 * (front normal = cross(p2 - p0, p1 - p0)).
 */
export function polyMesh(scene: Scene, name: string, verts: readonly Xyz[], faces: readonly (readonly number[])[], hex: string, opts: PolyOptions = {}): Mesh {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const centroid = { x: 0, y: 0, z: 0 };
  for (const v of verts) {
    centroid.x += v.x / verts.length;
    centroid.y += v.y / verts.length;
    centroid.z += v.z / verts.length;
  }
  for (const face of faces) {
    if (face.length < 3) continue;
    let order = face.map((i) => verts[i]!);
    // Newell normal (right-hand rule for counter-clockwise order).
    let nx = 0;
    let ny = 0;
    let nz = 0;
    const fc = { x: 0, y: 0, z: 0 };
    for (let i = 0; i < order.length; i++) {
      const a = order[i]!;
      const b = order[(i + 1) % order.length]!;
      nx += (a.y - b.y) * (a.z + b.z);
      ny += (a.z - b.z) * (a.x + b.x);
      nz += (a.x - b.x) * (a.y + b.y);
      fc.x += a.x / order.length;
      fc.y += a.y / order.length;
      fc.z += a.z / order.length;
    }
    // Babylon front normal for this winding is the negated right-hand normal.
    let bx = -nx;
    let by = -ny;
    let bz = -nz;
    const out = opts.outward ?? { x: fc.x - centroid.x, y: fc.y - centroid.y, z: fc.z - centroid.z };
    if (bx * out.x + by * out.y + bz * out.z < 0) {
      order = [...order].reverse();
      bx = -bx;
      by = -by;
      bz = -bz;
    }
    const len = Math.hypot(bx, by, bz) || 1;
    bx /= len;
    by /= len;
    bz /= len;
    const base = positions.length / 3;
    for (const v of order) {
      positions.push(v.x, v.y, v.z);
      normals.push(bx, by, bz);
      uvs.push(0, 0);
    }
    for (let k = 1; k + 1 < order.length; k++) indices.push(base, base + k, base + k + 1);
  }
  const mesh = new Mesh(name, scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.normals = normals;
  vd.uvs = uvs;
  vd.indices = indices;
  vd.applyToMesh(mesh, false);
  return applyPart(mesh, hex, opts);
}

/** Gable roof (ridge along z when `ridgeAlongZ`, else along x) with an overhang, centred at the origin in x/z. */
export function gableRoof(
  scene: Scene,
  name: string,
  width: number,
  depth: number,
  eaveY: number,
  ridgeY: number,
  overhang: number,
  hexSlope: string,
  hexEnd: string,
  opts: { ridgeAlongZ?: boolean; parent?: TransformNode | null; pos?: Partial<Xyz>; rot?: Partial<Xyz> } = {},
): Mesh[] {
  const hw = width / 2 + overhang;
  const hd = depth / 2 + overhang;
  const alongZ = opts.ridgeAlongZ ?? false;
  const base = { parent: opts.parent ?? null, pos: opts.pos, rot: opts.rot };
  if (alongZ) {
    const v: Xyz[] = [
      { x: -hw, y: eaveY, z: -hd },
      { x: hw, y: eaveY, z: -hd },
      { x: hw, y: eaveY, z: hd },
      { x: -hw, y: eaveY, z: hd },
      { x: 0, y: ridgeY, z: -hd },
      { x: 0, y: ridgeY, z: hd },
    ];
    const slopes = polyMesh(scene, `${name}_slopes`, v, [[0, 4, 5, 3], [1, 2, 5, 4], [0, 3, 2, 1]], hexSlope, base);
    const ends = polyMesh(scene, `${name}_ends`, v, [[0, 1, 4], [3, 5, 2]], hexEnd, base);
    return [slopes, ends];
  }
  const v: Xyz[] = [
    { x: -hw, y: eaveY, z: -hd },
    { x: hw, y: eaveY, z: -hd },
    { x: hw, y: eaveY, z: hd },
    { x: -hw, y: eaveY, z: hd },
    { x: -hw, y: ridgeY, z: 0 },
    { x: hw, y: ridgeY, z: 0 },
  ];
  const slopes = polyMesh(scene, `${name}_slopes`, v, [[0, 1, 5, 4], [3, 4, 5, 2], [0, 3, 2, 1]], hexSlope, base);
  const ends = polyMesh(scene, `${name}_ends`, v, [[0, 4, 3], [1, 2, 5]], hexEnd, base);
  return [slopes, ends];
}

/** Hip roof with a short ridge along x. */
export function hipRoof(
  scene: Scene,
  name: string,
  width: number,
  depth: number,
  eaveY: number,
  ridgeY: number,
  overhang: number,
  ridgeLength: number,
  hex: string,
  opts: { parent?: TransformNode | null; pos?: Partial<Xyz>; rot?: Partial<Xyz> } = {},
): Mesh {
  const hw = width / 2 + overhang;
  const hd = depth / 2 + overhang;
  const hr = Math.min(ridgeLength / 2, hw - 0.5);
  const v: Xyz[] = [
    { x: -hw, y: eaveY, z: -hd },
    { x: hw, y: eaveY, z: -hd },
    { x: hw, y: eaveY, z: hd },
    { x: -hw, y: eaveY, z: hd },
    { x: -hr, y: ridgeY, z: 0 },
    { x: hr, y: ridgeY, z: 0 },
  ];
  return polyMesh(scene, name, v, [[0, 1, 5, 4], [3, 4, 5, 2], [0, 4, 3], [1, 2, 5], [0, 3, 2, 1]], hex, { parent: opts.parent ?? null, pos: opts.pos, rot: opts.rot });
}

/** Pyramid (square base) used for tower crowns and tent-like shapes. */
export function pyramid(scene: Scene, name: string, width: number, depth: number, baseY: number, apexY: number, hex: string, opts: PartOptions = {}): Mesh {
  const hw = width / 2;
  const hd = depth / 2;
  const v: Xyz[] = [
    { x: -hw, y: baseY, z: -hd },
    { x: hw, y: baseY, z: -hd },
    { x: hw, y: baseY, z: hd },
    { x: -hw, y: baseY, z: hd },
    { x: 0, y: apexY, z: 0 },
  ];
  return polyMesh(scene, name, v, [[0, 1, 4], [1, 2, 4], [2, 3, 4], [3, 0, 4], [0, 3, 2, 1]], hex, opts);
}

/** Wedge: sloped top from the back edge (z = -d/2, height hBack) to the front edge (z = +d/2, height hFront). */
export function wedge(scene: Scene, name: string, w: number, d: number, hBack: number, hFront: number, hex: string, opts: PartOptions = {}): Mesh {
  const hw = w / 2;
  const hd = d / 2;
  const v: Xyz[] = [
    { x: -hw, y: 0, z: -hd },
    { x: hw, y: 0, z: -hd },
    { x: hw, y: 0, z: hd },
    { x: -hw, y: 0, z: hd },
    { x: -hw, y: hBack, z: -hd },
    { x: hw, y: hBack, z: -hd },
    { x: hw, y: hFront, z: hd },
    { x: -hw, y: hFront, z: hd },
  ];
  return polyMesh(
    scene,
    name,
    v,
    [
      [0, 1, 2, 3],
      [4, 7, 6, 5],
      [0, 4, 5, 1],
      [3, 2, 6, 7],
      [0, 3, 7, 4],
      [1, 5, 6, 2],
    ],
    hex,
    opts,
  );
}

/**
 * Merge coloured parts into a single mesh (world transforms baked in, sources disposed). Returns
 * null when there is nothing to merge.
 */
export function mergeParts(name: string, parts: Mesh[], nodesToDispose: TransformNode[] = []): Mesh | null {
  const live = parts.filter((p) => !p.isDisposed());
  if (live.length === 0) {
    for (const n of nodesToDispose) n.dispose();
    return null;
  }
  for (const p of live) p.computeWorldMatrix(true);
  const merged = Mesh.MergeMeshes(live, true, true, undefined, false, false);
  for (const n of nodesToDispose) n.dispose();
  if (!merged) return null;
  merged.name = name;
  merged.isPickable = false;
  return merged;
}

let unitBox: { positions: number[]; normals: number[]; indices: number[] } | null = null;
function unitBoxData() {
  if (!unitBox) {
    const vd = CreateBoxVertexData({ width: 1, height: 1, depth: 1 });
    unitBox = { positions: Array.from(vd.positions ?? []), normals: Array.from(vd.normals ?? []), indices: Array.from(vd.indices ?? []) };
  }
  return unitBox;
}

/**
 * Accumulates vertex-coloured geometry into flat arrays and builds ONE mesh at the end. Axis-aligned
 * (or y-rotated) boxes are written directly from a cached unit box, so a facade with hundreds of
 * window parts costs no intermediate Babylon meshes; complex parts (roofs, cylinders) can be added
 * as meshes, whose world transform is baked in and which are then disposed.
 */
export class PartBatch {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly colors: number[] = [];
  private readonly indices: number[] = [];
  /** Per-vertex bone index when skinned (one rigid influence per vertex). */
  private readonly bones: number[] | null;
  /** Bone that subsequently added parts are bound to (skinned batches only). */
  bone = 0;

  constructor(opts: { skinned?: boolean } = {}) {
    this.bones = opts.skinned ? [] : null;
  }

  get vertexCount(): number {
    return this.positions.length / 3;
  }

  private tagBones(count: number): void {
    if (!this.bones) return;
    for (let i = 0; i < count; i++) this.bones.push(this.bone);
  }

  /** Box centred at (x, y, z), optionally rotated about its own vertical axis. */
  box(w: number, h: number, d: number, hex: string, x: number, y: number, z: number, rotY = 0): this {
    const u = unitBoxData();
    const c = Color3.FromHexString(hex);
    const base = this.vertexCount;
    const cos = Math.cos(rotY);
    const sin = Math.sin(rotY);
    for (let i = 0; i < u.positions.length; i += 3) {
      const lx = u.positions[i]! * w;
      const ly = u.positions[i + 1]! * h;
      const lz = u.positions[i + 2]! * d;
      // Babylon RotationY: x' = x cos + z sin, z' = -x sin + z cos.
      this.positions.push(x + lx * cos + lz * sin, y + ly, z - lx * sin + lz * cos);
      const nx = u.normals[i]!;
      const ny = u.normals[i + 1]!;
      const nz = u.normals[i + 2]!;
      this.normals.push(nx * cos + nz * sin, ny, -nx * sin + nz * cos);
      this.colors.push(c.r, c.g, c.b, 1);
    }
    this.tagBones(u.positions.length / 3);
    for (const idx of u.indices) this.indices.push(base + idx);
    return this;
  }

  /** Box whose bottom sits at yBase. */
  boxBase(w: number, h: number, d: number, hex: string, x: number, yBase: number, z: number, rotY = 0): this {
    return this.box(w, h, d, hex, x, yBase + h / 2, z, rotY);
  }

  /** Bake a coloured mesh (world transform included) into the batch and dispose it. */
  add(mesh: Mesh): this {
    mesh.computeWorldMatrix(true);
    const wm = mesh.getWorldMatrix();
    const pos = mesh.getVerticesData(VertexBuffer.PositionKind);
    const nor = mesh.getVerticesData(VertexBuffer.NormalKind);
    const col = mesh.getVerticesData(VertexBuffer.ColorKind);
    const ind = mesh.getIndices();
    if (pos && nor && ind) {
      const base = this.vertexCount;
      const p = Vector3.Zero();
      const n = Vector3.Zero();
      for (let i = 0; i < pos.length / 3; i++) {
        Vector3.TransformCoordinatesFromFloatsToRef(pos[i * 3]!, pos[i * 3 + 1]!, pos[i * 3 + 2]!, wm, p);
        Vector3.TransformNormalFromFloatsToRef(nor[i * 3]!, nor[i * 3 + 1]!, nor[i * 3 + 2]!, wm, n);
        n.normalize();
        this.positions.push(p.x, p.y, p.z);
        this.normals.push(n.x, n.y, n.z);
        if (col) this.colors.push(col[i * 4]!, col[i * 4 + 1]!, col[i * 4 + 2]!, col[i * 4 + 3] ?? 1);
        else this.colors.push(1, 1, 1, 1);
      }
      this.tagBones(pos.length / 3);
      // A mirrored world matrix would flip the winding; none of our parts use negative scale.
      for (let i = 0; i < ind.length; i++) this.indices.push(base + ind[i]!);
    }
    mesh.dispose();
    return this;
  }

  addAll(meshes: readonly Mesh[]): this {
    for (const m of meshes) this.add(m);
    return this;
  }

  /**
   * Turn the vertices added since vertex `from` about the vertical axis through (cx, cz) by `yaw`
   * (Babylon RotationY convention). A rotation keeps the winding, so faces stay outward. Used to
   * build the world v3 south-side shops (fronts facing north) with the north-row facade code.
   */
  rotateY(cx: number, cz: number, yaw: number, from = 0): this {
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    for (let i = from * 3; i < this.positions.length; i += 3) {
      const dx = this.positions[i]! - cx;
      const dz = this.positions[i + 2]! - cz;
      this.positions[i] = cx + dx * c + dz * s;
      this.positions[i + 2] = cz - dx * s + dz * c;
      const nx = this.normals[i]!;
      const nz = this.normals[i + 2]!;
      this.normals[i] = nx * c + nz * s;
      this.normals[i + 2] = -nx * s + nz * c;
    }
    return this;
  }

  /** One mesh with positions, normals and vertex colours (null when empty). */
  build(name: string, scene: Scene): Mesh | null {
    if (this.indices.length === 0) return null;
    const mesh = new Mesh(name, scene);
    const vd = new VertexData();
    vd.positions = this.positions;
    vd.normals = this.normals;
    vd.colors = this.colors;
    vd.indices = this.indices;
    if (this.bones) {
      const mi = new Array<number>(this.bones.length * 4).fill(0);
      const mw = new Array<number>(this.bones.length * 4).fill(0);
      this.bones.forEach((b, i) => {
        mi[i * 4] = b;
        mw[i * 4] = 1;
      });
      vd.matricesIndices = mi;
      vd.matricesWeights = mw;
    }
    vd.applyToMesh(mesh, false);
    mesh.isPickable = false;
    return mesh;
  }
}

/** Fill a thin-instance matrix buffer from (x, z, yaw, scale) tuples. */
export function thinInstanceMatrices(items: readonly { x: number; y?: number; z: number; yaw: number; scale: number }[]): Float32Array {
  const buf = new Float32Array(items.length * 16);
  const m = new Matrix();
  items.forEach((it, i) => {
    Matrix.ComposeToRef(new Vector3(it.scale, it.scale, it.scale), Quaternion.RotationAxis(Vector3.Up(), it.yaw), new Vector3(it.x, it.y ?? 0, it.z), m);
    m.copyToArray(buf, i * 16);
  });
  return buf;
}

export function setThinInstances(mesh: Mesh, buf: Float32Array): void {
  mesh.thinInstanceSetBuffer("matrix", buf, 16, true);
  mesh.thinInstanceRefreshBoundingInfo(false);
}
